if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends HTMLElement {
        constructor() {
            super();

            this.input = this.querySelector('input[type="search"]');
            this.predictiveSearchResults = this.querySelector(
                '#predictive-search-target',
            );

            // Determine search type: 'native' (default) or 'metaobject'
            this.searchType = this.dataset.searchType || 'native';

            // Parse translations from data attribute
            this.translations = {};
            try {
                this.translations = JSON.parse(
                    this.dataset.translations || '{}',
                );
            } catch (e) {
                console.warn('Failed to parse translations:', e);
            }

            // Cache for metaobject data (only used for metaobject search)
            this.metaobjectData = null;
            this.dataLoaded = false;
            this.dataLoading = false;

            this.input.addEventListener(
                'input',
                this.debounce(() => this.onChange(), 300).bind(this),
            );

            // Lazy load metaobject data on focus if needed
            if (this.searchType === 'metaobject') {
                this.input.addEventListener(
                    'focus',
                    () => this.loadMetaobjectData(),
                    { once: true },
                );
            }
        }

        async loadMetaobjectData() {
            if (this.dataLoaded || this.dataLoading) return;

            this.dataLoading = true;

            try {
                const response = await fetch(
                    '/?section_id=predictive-search-data',
                );
                const text = await response.text();

                const doc = new DOMParser().parseFromString(text, 'text/html');
                const jsonText = doc
                    .querySelector('#shopify-section-predictive-search-data')
                    ?.textContent?.trim();

                if (jsonText) {
                    this.metaobjectData = JSON.parse(jsonText);
                    this.dataLoaded = true;
                }
            } catch (error) {
                console.error('Failed to load predictive search data:', error);
            }

            this.dataLoading = false;
        }

        onChange() {
            const searchTerm = this.input.value.trim();

            if (!searchTerm.length || searchTerm.length < 2) {
                this.close();
                return;
            }

            if (this.searchType === 'metaobject') {
                this.getMetaobjectResults(searchTerm);
            } else {
                this.getNativeResults(searchTerm);
            }
        }

        // Native search: products, articles, pages via /search/suggest
        getNativeResults(searchTerm) {
            const params = new URLSearchParams({
                q: searchTerm,
                'resources[type]': 'product,article,page',
                'resources[options][fields]':
                    'title,body,product_type,tag,variants.title',
                section_id: 'predictive-search-results',
            });

            fetch(`/search/suggest?${params.toString()}`)
                .then((response) => {
                    if (!response.ok) {
                        this.close();
                        throw new Error(
                            `${response.status} ${response.statusText}`,
                        );
                    }
                    return response.text();
                })
                .then((text) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(text, 'text/html');

                    const section = doc.querySelector(
                        '#shopify-section-predictive-search-results',
                    );

                    if (section) {
                        this.predictiveSearchResults.innerHTML =
                            section.innerHTML;
                        this.open();
                    } else {
                        this.close();
                    }
                })
                .catch((error) => {
                    console.error('Native search error:', error);
                    this.close();
                });
        }

        // Metaobject search: FAQ, FAQ collections
        async getMetaobjectResults(searchTerm) {
            // Ensure data is loaded
            if (!this.dataLoaded) {
                await this.loadMetaobjectData();
            }

            if (!this.metaobjectData) {
                this.close();
                return;
            }

            const results = {
                faq: this.filterItems(this.metaobjectData.faq, searchTerm),
                faq_collection: this.filterItems(
                    this.metaobjectData.faq_collection,
                    searchTerm,
                ),
            };

            this.renderMetaobjectResults(results, searchTerm);
        }

        filterItems(items, searchTerm) {
            if (!items || !Array.isArray(items)) return [];

            const terms = searchTerm.toLowerCase().split(/\s+/);

            return items
                .map((item) => {
                    // Search in BOTH title AND content
                    const searchableText = [
                        item.title || '',
                        item.content || '',
                    ]
                        .join(' ')
                        .toLowerCase();

                    // Check if all terms match
                    const allMatch = terms.every((term) =>
                        searchableText.includes(term),
                    );
                    if (!allMatch) return null;

                    // Score: title matches rank higher
                    const titleLower = (item.title || '').toLowerCase();
                    const titleMatch = terms.some((term) =>
                        titleLower.includes(term),
                    );

                    return {
                        ...item,
                        score: titleMatch ? 0 : 1,
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.score - b.score)
                .slice(0, 5);
        }

        renderCategorySection(items, titleKey, defaultTitle, translations) {
            if (!items || items.length === 0) return '';

            // CSS classes - centralized for easy modification
            const CATEGORY_CLASSES =
                'predictive-search-category mb-8 pb-5 border-b';
            const HEADING_CLASSES =
                'text-xs font-mono uppercase mb-3 text-gray-500';
            const LIST_CLASSES = 'space-y-2';
            const ITEM_CLASSES = 'text-base py-3 mb-0';
            const LINK_CLASSES = 'truncate';

            const title = this.escapeHtml(
                translations[titleKey] || defaultTitle,
            );
            let html = `
                <div class="${CATEGORY_CLASSES}">
                    <h3 class="${HEADING_CLASSES}">${title}</h3>
                    <ul role="listbox" class="${LIST_CLASSES}">
            `;

            for (const item of items) {
                html += `
                    <li role="option" class="${ITEM_CLASSES}">
                        <a href="${this.escapeHtml(item.url)}" class="${LINK_CLASSES}">${this.escapeHtml(item.title)}</a>
                    </li>
                `;
            }

            html += '</ul></div>';
            return html;
        }

        renderMetaobjectResults(results, searchTerm) {
            const t = this.translations;
            const hasResults =
                results.faq.length > 0 || results.faq_collection.length > 0;

            if (!hasResults) {
                this.predictiveSearchResults.innerHTML = `
                    <div id="predictive-search-results" tabindex="-1">
                        <p class="text-gray-500">${this.escapeHtml(t.no_results || 'No results found for')} "${this.escapeHtml(searchTerm)}"</p>
                        <button type="submit">${this.escapeHtml(t.search_for || 'Search for')} "${this.escapeHtml(searchTerm)}"</button>
                    </div>
                `;
                this.open();
                return;
            }

            let html = '<div id="predictive-search-results" tabindex="-1">';

            // Render all categories using the helper function
            html += this.renderCategorySection(results.faq, 'faq', 'FAQ', t);
            html += this.renderCategorySection(
                results.faq_collection,
                'faq_categories',
                'FAQ Categories',
                t,
            );

            html += `<button type="submit">${this.escapeHtml(t.search_for || 'Search for')} "${this.escapeHtml(searchTerm)}"</button></div>`;

            this.predictiveSearchResults.innerHTML = html;
            this.open();
        }

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text || '';
            return div.innerHTML;
        }

        open() {
            this.predictiveSearchResults.style.display = 'block';
            this.input.setAttribute('aria-expanded', 'true');
        }

        close() {
            this.predictiveSearchResults.style.display = 'none';
            this.input.setAttribute('aria-expanded', 'false');
        }

        debounce(fn, wait) {
            let t;
            return (...args) => {
                clearTimeout(t);
                t = setTimeout(() => fn.apply(this, args), wait);
            };
        }
    }

    customElements.define('predictive-search', PredictiveSearch);
}
